/**
 * 거래처 담당자 주소록 API — backend /partner-contacts/* (v2.143.0 · 2026-09-09)
 *
 * 영업진행 담당자 칸 · 딜등록 제안사 명단 · 딜 제안사 칸 원문을 모은 한 장부. 거래처(partner_company)에 연결된다.
 * 쓰는 곳: 거래처 관리 › [👤 거래처 담당자] 탭 · 딜등록 창(제안사 담당자 고르기) · 정보 요청 창 · 영업진행 폼 자동완성.
 */

import { api } from './api';

export type ContactSource = 'manual' | 'sales' | 'deal_xlsx' | 'deal_contact' | 'xlsx';

export const SOURCE_LABEL: Record<ContactSource, string> = {
  manual: '직접 입력',
  sales: '영업진행',
  deal_xlsx: '딜등록 엑셀',
  deal_contact: '딜등록 명단',
  xlsx: '엑셀 올리기',
};

export interface PartnerContact {
  id: number;
  /** 거래처 목록의 회사 번호 — null 이면 「미연결」(글자만) */
  partnerCompanyId: number | null;
  partnerName: string;
  partnerKind: string;
  /** 화면에 보이는 회사 이름 — 연결됐으면 거래처 이름, 아니면 적힌 글자 */
  companyName: string;
  companyNameRaw: string;
  companyNameEn: string;
  linked: boolean;
  name: string;
  department: string;
  title: string;
  /** 「이름 직급」 */
  label: string;
  email: string;
  mobile: string;
  phone: string;
  note: string;
  isActive: boolean;
  source: ContactSource;
  sourceRef: string;
  lastRequestAt: string | null;
  requestCount: number;
  dealCount: number;
  salesCount: number;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerContactInput {
  partnerCompanyId: number | null;
  companyName: string;
  companyNameEn: string;
  name: string;
  department: string;
  title: string;
  email: string;
  mobile: string;
  phone: string;
  note: string;
  isActive: boolean;
  /** 같은 회사·같은 이름 경고(409)를 무시하고 넣는다 — 동명이인 */
  force?: boolean;
}

export interface ContactCounts {
  total: number;
  active: number;
  unlinked: number;
  companies: number;
}

export interface PartnerCompanyOption {
  id: number;
  name: string;
  kind: string;
  isActive: boolean;
}

export interface ContactSuggestion {
  id: number;
  name: string;
  title: string;
  department: string;
  email: string;
  mobile: string;
  /** 「이름 직급」 — 영업진행 폼의 담당자 칸에 그대로 들어간다 */
  label: string;
}

export function emptyContactInput(): PartnerContactInput {
  return {
    partnerCompanyId: null, companyName: '', companyNameEn: '', name: '', department: '', title: '',
    email: '', mobile: '', phone: '', note: '', isActive: true,
  };
}

export async function listContacts(p: { q?: string; companyId?: number; unlinked?: boolean; inactive?: boolean } = {}): Promise<{ rows: PartnerContact[]; counts: ContactCounts }> {
  const qs = new URLSearchParams();
  if (p.q) qs.set('q', p.q);
  if (p.companyId) qs.set('companyId', String(p.companyId));
  if (p.unlinked) qs.set('unlinked', '1');
  if (p.inactive) qs.set('inactive', '1');
  const s = qs.toString();
  return api.get<{ rows: PartnerContact[]; counts: ContactCounts }>(`/partner-contacts${s ? '?' + s : ''}`);
}

export async function listContactCompanies(): Promise<PartnerCompanyOption[]> {
  const r = await api.get<{ rows: PartnerCompanyOption[] }>('/partner-contacts/companies');
  return r.rows;
}

/** 영업진행 폼 — 제안업체 칸의 회사 담당자 후보 */
export async function suggestContacts(company: string): Promise<ContactSuggestion[]> {
  if (!company.trim()) return [];
  const r = await api.get<{ rows: ContactSuggestion[] }>(`/partner-contacts/suggest?company=${encodeURIComponent(company.trim())}`);
  return r.rows;
}

export async function createContact(input: PartnerContactInput): Promise<PartnerContact> {
  return api.post<PartnerContact>('/partner-contacts', input);
}

export async function updateContact(id: number, input: PartnerContactInput): Promise<PartnerContact> {
  return api.patch<PartnerContact>(`/partner-contacts/${id}`, input);
}

export async function deleteContact(id: number): Promise<void> {
  await api.delete<{ deleted: boolean }>(`/partner-contacts/${id}`);
}

/** 연락처 한 줄 표시 — 휴대전화 · 전화 중 있는 것 */
export function contactPhones(c: Pick<PartnerContact, 'mobile' | 'phone'>): string {
  return [c.mobile, c.phone].filter(Boolean).join(' · ');
}

/* ── 엑셀 올리기 (v2.144.0) — 1단계 미리보기 → 2단계 적용 ── */

export interface ImportItem {
  rowNo: number;
  action: 'insert' | 'update' | 'skip';
  reason: string;
  companyName: string;
  companyNameEn: string;
  partnerCompanyId: number | null;
  companyLinked: boolean;
  name: string;
  title: string;
  department: string;
  email: string;
  mobile: string;
  phone: string;
  note: string;
  isActive: boolean | null;
  existingId: number | null;
  changes: { field: string; label: string; from: string; to: string }[];
}

export interface ImportPreview {
  filename: string;
  sheetName: string;
  headerMap: { header: string; field: string }[];
  warnings: string[];
  rows: number;
  items: ImportItem[];
  counts: { insert: number; update: number; skip: number };
}

/** 파일을 올려 판정만 받는다 — 자료는 바뀌지 않는다. ⚠api 래퍼는 JSON 만 다뤄서 fetch 로 직접 */
export async function previewContactImport(file: File): Promise<ImportPreview> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/partner-contacts/import/preview', { method: 'POST', credentials: 'include', body: form });
  const json = await res.json().catch(() => null) as { ok: boolean; data?: ImportPreview; error?: { message?: string } } | null;
  if (!json || !json.ok || !json.data) throw new Error(json?.error?.message ?? `올리기 실패 (HTTP ${res.status})`);
  return json.data;
}

export async function applyContactImport(items: ImportItem[]): Promise<{ inserted: number; updated: number; missing: number; duplicate?: number }> {
  return api.post<{ inserted: number; updated: number; missing: number; duplicate?: number }>('/partner-contacts/import/apply', { items });
}
